import React, { useState, useEffect, useRef } from 'react'
import { useAppLanguage } from '../../i18n'

type TeamCreateModalProps = {
  open: boolean
  onClose: () => void
  onCreate: (title: string) => Promise<boolean>
}

/**
 * Modal de création d'une nouvelle équipe d'agents
 * Suit le même pattern que CollectionCreateModal
 */
export default function TeamCreateModal({
  open,
  onClose,
  onCreate,
}: TeamCreateModalProps) {
  const { t } = useAppLanguage()
  const [title, setTitle] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset et focus à l'ouverture
  useEffect(() => {
    if (open) {
      setTitle('')
      setError(null)
      setSaving(false)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  // Fermer avec Escape
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setError(t.agents.teams.createModal.errorRequired)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const success = await onCreate(title.trim())
      if (success) {
        onClose()
      } else {
        setError(t.agents.teams.createModal.errorCreate)
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || t.agents.teams.createModal.errorCreate)
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      {/* Modal */}
      <aside className="absolute inset-y-0 right-0 w-full max-w-md border-l border-emerald-100/70 bg-white/95 shadow-2xl dark:border-emerald-900/50 dark:bg-zinc-950/95">
        <div className="flex h-full flex-col">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-emerald-100/70 px-6 py-4 dark:border-emerald-900/50">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                {t.agents.teams.createModal.title}
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {t.agents.teams.createModal.subtitle}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-zinc-200/70 px-3 py-1 text-sm text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              {t.agents.teams.createModal.close}
            </button>
          </header>

          {/* Contenu */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            <section className="space-y-3">
              <label 
                htmlFor="team-title"
                className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400"
              >
                {t.agents.teams.createModal.nameLabel}
              </label>
              <input
                ref={inputRef}
                id="team-title"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t.agents.teams.createModal.namePlaceholder}
                disabled={saving}
                className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/50"
                data-onboarding="team-name-input"
              />
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
              <button
                type="submit"
                disabled={saving || !title.trim()}
                className="inline-flex w-full justify-center rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:opacity-50"
                data-onboarding="create-team-btn"
              >
                {saving ? t.agents.teams.createModal.creating : t.agents.teams.createModal.create}
              </button>
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                {t.agents.teams.createModal.tipsTitle}
              </h3>
              <ul className="space-y-3 text-sm text-zinc-600 dark:text-zinc-300">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {t.agents.teams.createModal.tip1}
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {t.agents.teams.createModal.tip2}
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  {t.agents.teams.createModal.tip3}
                </li>
              </ul>
            </section>
          </form>
        </div>
      </aside>
    </div>
  )
}
